/**
 * Boot and game loop.
 *
 * WebGPU where available, WebGL2 otherwise — WebGPURenderer handles the
 * fallback itself and the TSL materials compile to whichever backend wins.
 * Physics runs on a fixed 60 Hz accumulator so towing behaves identically on a
 * 30 Hz laptop and a 144 Hz monitor; rendering interpolation is deliberately
 * skipped because at these speeds the half-frame error is invisible.
 *
 * The core here is mode-agnostic: it owns the sea, sky, tug, hawser, camera,
 * HUD, audio and weather, and delegates the world, the towed body and the
 * objective to a GameMode chosen on the title screen.
 */

import './style.css'
import { ACESFilmicToneMapping, DirectionalLight, Fog, HemisphereLight, Scene, WebGPURenderer } from 'three/webgpu'

import { Hawser } from './entities/Hawser.ts'
import { Tug } from './entities/Tug.ts'
import { ChaseCamera } from './game/ChaseCamera.ts'
import { EngineSound } from './game/EngineSound.ts'
import { WaypointArrow } from './game/WaypointArrow.ts'
import { Weather } from './game/Weather.ts'
import { UpdateChecker } from './game/UpdateChecker.ts'
import { showTitleScreen, type GameSelection } from './game/TitleScreen.ts'
import { HarbourMode } from './game/HarbourMode.ts'
import { BeachMode } from './game/BeachMode.ts'
import { IcebreakerMode } from './game/IcebreakerMode.ts'
import { LandingMode } from './game/LandingMode.ts'
import { SeaTraffic } from './world/SeaTraffic.ts'
import { PlayerWake } from './world/PlayerWake.ts'
import { seaState } from './world/waves.ts'
import { OpenWaterMode } from './game/OpenWaterMode.ts'
import type { GameMode, ModeKind } from './game/GameMode.ts'
import 'lucide-static/font/lucide.css'
import { Hud } from './game/Hud.ts'
import { Input } from './game/Input.ts'
import { RuntimeModelDetail } from './game/RuntimeModelDetail.ts'
import { RenderQuality, useMobileQuality } from './game/RenderQuality.ts'
import { Ocean } from './world/Ocean.ts'
import { HorizonMountains } from './world/HorizonMountains.ts'
import { Rain } from './world/Rain.ts'
import { Sky, SUN_DIRECTION, lightningUniform, stormUniform } from './world/Sky.ts'
import { syncWaveUniforms } from './world/waves.ts'

// Native selection, image dragging and long-press menus interrupt play.
for (const eventName of ['selectstart', 'dragstart', 'contextmenu']) {
  document.addEventListener(eventName, (event) => {
    if (event.target instanceof HTMLElement && event.target.closest('input, textarea, [contenteditable]')) return
    event.preventDefault()
  })
}

const FIXED_DT = 1 / 60

async function boot(selection: GameSelection): Promise<void> {
  const bootScreen = document.getElementById('boot')!
  bootScreen.classList.remove('hidden')
  const canvas = document.getElementById('viewport') as HTMLCanvasElement

  // Millimetre surface detail must remain distinct at harbour-scale camera distances.
  // Allow explicit fallback testing without changing browser GPU settings.
  const forceWebGL = new URLSearchParams(window.location.search).get('renderer') === 'webgl'
  const params = new URLSearchParams(window.location.search)
  const quality = new RenderQuality(useMobileQuality(navigator.userAgent, navigator.maxTouchPoints, params.get('quality')))
  const renderer = new WebGPURenderer({ canvas, antialias: true, logarithmicDepthBuffer: true, forceWebGL })
  renderer.toneMapping = ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.05
  renderer.setPixelRatio(quality.pixelRatio(window.innerWidth, window.innerHeight, window.devicePixelRatio))
  renderer.setSize(window.innerWidth, window.innerHeight)
  await renderer.init()
  canvas.dataset.renderer = 'isWebGPUBackend' in renderer.backend && renderer.backend.isWebGPUBackend ? 'webgpu' : 'webgl'

  // --- shared scene --------------------------------------------------------

  const scene = new Scene()

  const sky = new Sky()
  scene.add(sky.mesh)

  const mountains = new HorizonMountains()
  scene.add(mountains.mesh)

  const ocean = new Ocean(quality.mobile)
  scene.add(ocean.mesh)

  const rain = new Rain()
  scene.add(rain.mesh)

  const sun = new DirectionalLight('#fff3dd', 3.2)
  sun.position.copy(SUN_DIRECTION).multiplyScalar(400)
  scene.add(sun)
  const hemi = new HemisphereLight('#a8c2d4', '#1d3a4a', 0.9)
  scene.add(hemi)

  let tug = new Tug(selection.vessel)
  await tug.ready
  scene.add(tug.group)

  const hawser = new Hawser()
  scene.add(hawser.mesh)

  // --- ui / control --------------------------------------------------------

  const input = new Input()
  const hud = new Hud(document.getElementById('ui')!)
  const releaseControls = hud.bindControls(input)
  const waypoint = new WaypointArrow(document.getElementById('ui')!)
  const weather = new Weather()
  new UpdateChecker(document.getElementById('ui')!).start()

  const engineSound = new EngineSound()
  engineSound.setVessel(tug.kind)
  weather.onThunder = (intensity) => engineSound.thunder(intensity)
  const unlockAudio = (): void => engineSound.unlock()
  window.addEventListener('keydown', unlockAudio, { once: false })
  window.addEventListener('pointerdown', unlockAudio, { once: false })

  const chase = new ChaseCamera(window.innerWidth / window.innerHeight, canvas)
  chase.setVesselLength(tug.config.length, tug.forwardView)

  window.addEventListener('resize', () => {
    renderer.setPixelRatio(quality.pixelRatio(window.innerWidth, window.innerHeight, window.devicePixelRatio))
    renderer.setSize(window.innerWidth, window.innerHeight)
    chase.setAspect(window.innerWidth / window.innerHeight)
  })

  // --- mode ----------------------------------------------------------------

  const ctx = { scene, tug, hawser, hud, waypoint, chase, engineSound }
  const modelDetail = new RuntimeModelDetail()
  let mode!: GameMode
  let currentKind: ModeKind = selection.mode
  let traffic: SeaTraffic | null = null
  let lastTrafficImpact = -Infinity

  // Boot (or re-boot) a mode in place — no page reload, so the renderer, sea,
  // sky and tug all stay warm across a switch. Everything the previous mode
  // touched (its world, the tow, the tug's seabed and motion, held inputs) is
  // wiped first so the next mode starts from a clean slate.
  const enterMode = (kind: ModeKind): void => {
    quality.resetSampling()
    traffic?.dispose()
    traffic = null
    lastTrafficImpact = -Infinity
    currentKind = kind
    scene.fog = kind === 'icebreaker' ? new Fog('#bdcdd7', 250, 850) : null
    hawser.release()
    weather.condition = 'calm'
    tug.bedSampler = null
    tug.config.groundFriction = 0
    tug.velocity.set(0, 0, 0)
    tug.yawRate = 0
    tug.pitch = 0
    tug.roll = 0
    tug.pitchRate = 0
    tug.rollRate = 0
    tug.damage = 0
    tug.throttle = 0
    tug.rudder = 0
    tug.ramp?.reset()
    input.reset()

    hud.setFreeDrive(kind === 'open-water' || kind === 'icebreaker')
    hud.setIceMode(kind === 'icebreaker')
    hud.setWeather('calm')
    hud.setView(chase.viewName)
    if (kind === 'icebreaker') { seaState.swell = .2; seaState.steepness = .1 }
    mode = kind === 'icebreaker' ? new IcebreakerMode(ctx) : kind === 'open-water' ? new OpenWaterMode(ctx) : kind === 'beach' ? new BeachMode(ctx) : kind === 'landing' ? new LandingMode(ctx) : new HarbourMode(ctx)
    scene.add(mode.worldGroup)
    modelDetail.track(mode.worldGroup, { detailDistance: 360, shadowDistance: 700 })
    modelDetail.track(tug.group, { detailDistance: 300, shadowDistance: 520, preserveGeometry: tug.kind === 'container-ship' || tug.kind === 'aircraft-carrier' })
    tug.position.copy(mode.tugSpawn.position)
    tug.yaw = mode.tugSpawn.heading
    chase.snapBehind(tug.position, tug.yaw)
    mode.start()
    if (kind === 'open-water' || kind === 'harbour') {
      const harbour = mode instanceof HarbourMode ? mode : null
      const barge = harbour?.getBarge()
      const obstacles = harbour?.trafficObstacles ?? (mode instanceof OpenWaterMode ? mode.trafficObstacles : [])
      traffic = new SeaTraffic(tug, obstacles, undefined, barge ? [barge] : [])
      scene.add(traffic.group)
    }
  }

  enterMode(selection.mode)

  // While the title screen is up (via MENU) the whole simulation is paused —
  // physics, rendering and audio all stop, so nothing keeps running behind it.
  let paused = false

  hud.bindMenu(() => {
    if (paused) return
    paused = true
    hud.setMenuOpen(true)
    releaseControls()
    engineSound.suspend()
    input.reset()
    void showTitleScreen(tug.kind, currentKind).then(async (choice) => {
      bootScreen.classList.remove('hidden')
      let replacement: Tug | null = null
      try {
        if (choice.vessel !== tug.kind) {
          replacement = new Tug(choice.vessel)
          await replacement.ready
        }
        // Keep the existing game intact until the new model has loaded.
        hawser.release()
        mode.dispose()
        if (replacement) {
          scene.remove(tug.group)
          tug.dispose()
          tug = replacement
          ctx.tug = tug
          engineSound.setVessel(tug.kind)
          scene.add(tug.group)
        }
        chase.setVesselLength(tug.config.length, tug.forwardView)
        enterMode(choice.mode)
      } catch (error) {
        replacement?.dispose()
        console.error('Could not change vessel:', error)
        hud.announce('Could not load that vessel. Try again from MENU.', 6)
      } finally {
        input.reset()
        bootScreen.classList.add('hidden')
        engineSound.resume()
        paused = false
        hud.setMenuOpen(false)
      }
    })
  })

  hud.bindMute(() => {
    engineSound.setMuted(!engineSound.isMuted)
    return engineSound.isMuted
  })

  // Dev handle: lets tests (and the curious) drive from the console. `mode` is
  // a live getter so it survives a menu switch.
  if (import.meta.env.DEV) {
    Object.assign(window, {
      hawser: {
        get tug() { return tug },
        input,
        hawserLine: hawser,
        engineSound,
        weather,
        get traffic() { return traffic },
        get mode() {
          return mode
        },
        hook: () => mode.toggleHook(),
        getBarge: () => (mode as HarbourMode).getBarge?.() ?? null,
      },
    })
  }

  // --- loop ----------------------------------------------------------------

  let elapsed = 0
  let accumulator = 0
  let previousTime = performance.now()
  const playerWake = new PlayerWake()
  // Opt-in, visible diagnostics work on the actual iPad without devtools.
  const stats = params.has('perf') ? document.createElement('output') : null
  if (stats) {
    stats.style.cssText = 'position:fixed;top:110px;left:12px;z-index:1000;background:#102330dd;color:white;padding:8px;font:12px monospace;pointer-events:none;white-space:pre'
    document.body.append(stats)
  }
  let statsTime = 0, statsFrames = 0

  const frame = (now: number): void => {
    requestAnimationFrame(frame)

    // Paused on the menu: keep the clock fresh so there's no dt jump on resume,
    // but run no physics, rendering or input.
    if (paused || document.hidden) {
      previousTime = now
      quality.resetSampling()
      statsTime = 0; statsFrames = 0
      return
    }

    // Limit sustained tablet GPU load. Physics still advances at fixed 60 Hz.
    // A little tolerance avoids skipping alternating refreshes due to jitter.
    if (quality.mobile && now - previousTime < 1000 / 30 - 1.5) return

    const rawDt = (now - previousTime) / 1000
    if (quality.sample(rawDt)) renderer.setPixelRatio(quality.pixelRatio(window.innerWidth, window.innerHeight, window.devicePixelRatio))
    const frameDt = Math.min(rawDt, 0.1)
    previousTime = now
    accumulator += frameDt

    input.update(frameDt)
    tug.throttle = input.throttle
    tug.rudder = input.rudder

    if (input.wasPressed('Space')) mode.toggleHook()
    if (input.wasPressed('KeyE') && tug.ramp) {
      const locked = mode.rampLocked?.()
      if (locked) hud.announce(locked, 2.5)
      else hud.announce(tug.ramp.toggle() ? 'Ramp down' : 'Ramp up', 1.5)
    }
    if (input.wasPressed('KeyC')) {
      chase.cycleZoom()
      hud.setView(chase.viewName)
      hud.announce(`View: ${chase.viewName}`, 2)
    }
    if (input.wasPressed('KeyV') && currentKind !== 'icebreaker') {
      const condition = weather.cycle()
      hud.setWeather(condition)
      const message =
        condition === 'calm'
          ? 'Weather: calm'
          : condition === 'rough'
            ? 'Weather: rough'
            : condition === 'storm'
              ? 'STORM WARNING'
              : 'PERFECT STORM — hold on!'
      hud.announce(message, 2.5)
    }

    // Physics and the rendered surface must see the same sea state, including
    // during the transition into a storm.
    weather.update(frameDt)
    if (currentKind === 'icebreaker') { seaState.swell = .2; seaState.steepness = .1 }

    while (accumulator >= FIXED_DT) {
      accumulator -= FIXED_DT
      elapsed += FIXED_DT

      // Hawser first: its forces sit in each body's accumulator through that
      // body's own step (which clears the accumulator only at the end).
      if (hawser.step()) hud.announce('THE LINE PARTED — ease up next time', 5)
      tug.step(FIXED_DT, elapsed)
      mode.fixedStep(FIXED_DT, elapsed)
      if (traffic) {
        const barge = mode instanceof HarbourMode ? mode.getBarge() : null
        if (traffic.step(FIXED_DT, elapsed, barge ? [barge] : []) && elapsed-lastTrafficImpact > 2) {
          lastTrafficImpact = elapsed
          hud.announce('Collision — give the small boats more room', 3)
          engineSound.crash()
        }
      }
    }

    // Visual sync.
    stormUniform.value = weather.stormFactor
    lightningUniform.value = weather.flashValue
    sun.intensity = 3.2 * (1 - weather.stormFactor * 0.75) + weather.flashValue * 4
    hemi.intensity = 0.9 * (1 - weather.stormFactor * 0.45) + weather.flashValue * 2
    syncWaveUniforms(elapsed)
    tug.syncVisual()
    tug.ramp?.update(frameDt, tug, mode.groundHeight ? (x, z) => mode.groundHeight!(x, z) : null)
    hawser.updateVisual(elapsed)

    chase.update(tug.position, tug.yaw, hawser.connected ? hawser.toPosition : null, frameDt, elapsed, tug.quaternion)
    sky.update(chase.camera.position)
    mountains.update(chase.camera.position)
    ocean.update(chase.camera.position)
    rain.update(chase.camera.position, weather.stormFactor)
    ocean.updateWakes([
      playerWake.update(tug),
      mode.towWake() ?? { x: 0, z: 0, yaw: 0, speed: 0, wash: 0, halfBeam: 1 },
    ])

    hud.setRamp(tug.ramp ? tug.ramp.open : null)
    hud.setThrottle(tug.throttle)
    hud.setRudder(tug.rudder)
    hud.setSpeedHeading(tug.speed, tug.yaw)
    hud.setTension(hawser.tension, hawser.connected)
    traffic?.frame()
    mode.frame(elapsed, frameDt)
    const barge = mode instanceof HarbourMode ? mode.getBarge() : null
    if (barge) modelDetail.track(barge.group, { detailDistance: 320, shadowDistance: 600 })
    modelDetail.update(chase.camera.position, elapsed)
    ocean.hullMasks.update([tug.waterExclusion, ...(mode.waterExclusions?.() ?? []), ...(traffic?.waterExclusions() ?? [])])
    hud.update(frameDt)

    engineSound.update(tug.throttle, Math.min(hawser.tension / 600_000, 1), tug.speed)
    engineSound.setRain(weather.stormFactor)

    renderer.render(scene, chase.camera)
    if (stats && rawDt > 0 && rawDt < .5) {
      statsTime += rawDt; statsFrames++
      if (statsTime >= 1) {
        stats.textContent = `${(statsFrames/statsTime).toFixed(0)} FPS · ${quality.mobile ? 'tablet' : 'high'} · ${canvas.dataset.renderer}\n${canvas.width} × ${canvas.height} · ${renderer.info.render.drawCalls} draws · ${Math.round(renderer.info.render.triangles/1000)}k triangles`
        statsTime = 0; statsFrames = 0
      }
    }
  }

  bootScreen.classList.add('hidden')
  requestAnimationFrame(frame)
}

async function main(): Promise<void> {
  const modeKind = await showTitleScreen()
  await boot(modeKind)
}

main().catch((error: unknown) => {
  console.error(error)
  const status = document.querySelector('.boot-status')
  if (status) {
    status.textContent = `Failed to start: ${error instanceof Error ? error.message : String(error)}. WebGPU or WebGL2 is required.`
    status.classList.add('error')
  }
  document.getElementById('boot')?.classList.remove('hidden')
  const retry = document.createElement('button')
  retry.className = 'hud-btn hud-btn-wide'
  retry.textContent = 'Back to menu'
  retry.onclick = () => window.location.reload()
  document.querySelector('.boot-inner')?.append(retry)
})
